import { useTheme } from '@emotion/react'
import styled from '@emotion/styled'
import Link from 'next/link'
import React from 'react'

import { Card as FeatureCard } from 'components/FeatureCard'
import { OpenInNewIcon } from 'components/icons'
import { formatDate } from 'lib/formatDate'
import { Feature } from 'types/feature'
import { Place } from 'types/place'

const Card = styled(FeatureCard)`
	background-color: ${({ theme }) => theme.colors.light.grayscale[15]};
	gap: ${({ theme }) => theme.spaces.space4};
	padding: ${({ theme }) => theme.spaces.space5};
	border-radius: ${({ theme }) => theme.borderRadiuses.xxl};
	border: 1px solid ${({ theme }) => theme.colors.light.borderColor};
`

const Container = styled.div`
	width: 100%;
	height: 100%;
	display: flex;
	flex-direction: row;
	justify-content: space-between;
`

const Content = styled.div`
	display: flex;
	gap: 8px;
	max-width: 90%;
	flex-direction: column;
	justify-content: space-between;
`

const ExternalLink = styled(Link)`
	width: 20px;
`

const Footer = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	align-items: center;
`

const Name = styled.h3`
	margin: 0;
	font-weight: 700;
	font-size: ${({ theme }) => theme.fontSizes.size14};
`

const Rating = styled.div`
	display: flex;
	flex-direction: row;
	gap: 4px;
	font-size: ${({ theme }) => theme.fontSizes.size14};
`

const Details = styled.div`
	display: flex;
	flex-direction: column;
	color: ${({ theme }) => theme.colors.light.alternativeTextColor};
	${({ theme }) => theme.typography.caption};
`

const DetailsItem = styled.span`
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
`

const Status = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
	color: #7a7a7a;
	gap: ${({ theme }) => theme.spaces.space4};
	${({ theme }) => theme.typography.smallcaps};
`

const StatusIcon = styled.div`
	width: 12px;
	height: 12px;
	border-radius: ${({ theme }) => theme.borderRadiuses.round};
	background-color: ${({ theme }) => theme.colors.light.success};
`

const ButtonsOutlet = styled.div`
	button {
		padding: 0;
	}
`

type Props = Place &
	Partial<Pick<Feature<'google_reviews'>, 'updatedAt'>> & {
		buttons?: React.ReactNode
	}

function PlaceCard({ name, rating, url, userRatingsTotal, formattedAddress, website, updatedAt, buttons }: Props) {
	const theme = useTheme()

	const formattedUpdatedAt = React.useMemo(() => {
		if (!updatedAt) {
			return null
		}

		return formatDate(updatedAt)
	}, [updatedAt])

	return (
		<Card data-testid="place-card">
			<Container>
				<Content>
					<Name>{name}</Name>
					<Rating>
						<b>{rating.toFixed(1)}</b>
						<span>
							({userRatingsTotal} {userRatingsTotal === 1 ? 'review' : 'reviews'})
						</span>
					</Rating>
					<Details>
						{formattedAddress && <DetailsItem>{formattedAddress}</DetailsItem>}
						{website && <DetailsItem>{website}</DetailsItem>}
					</Details>
				</Content>
				<ExternalLink data-testid="place-url" href={url} rel="noopener,noreferrer" target="_blank">
					<OpenInNewIcon size={16} color={theme.colors.light.primaryTextColor} />
				</ExternalLink>
			</Container>
			{formattedUpdatedAt && buttons && (
				<Footer>
					<Status>
						<StatusIcon />
						<span>{`Updated: ${formattedUpdatedAt.day}, ${formattedUpdatedAt.time}`}</span>
					</Status>
					<ButtonsOutlet>{buttons}</ButtonsOutlet>
				</Footer>
			)}
		</Card>
	)
}

export default PlaceCard
